import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import OverridesIndicator from '../components/project/OverridesIndicator';
import NewSinceLastCRCBadge from '../components/project/NewSinceLastCRCBadge';
import { OBJETS, ROLE_PROJET, BOUTONS, ETAPES_CHANTIER, ETAPE_CLASSE, STATUT_CHANTIER, etapeChantier, libelleEtape } from '../lib/libelles';
// Fluidité B2 — 6 onglets qui suivent le chantier, anciens liens redirigés
import { ONGLETS_CHANTIER, VUES_CHANTIER, resoudreOnglet, ongletHorsEtape } from '../lib/ongletsChantier';
import Layout from '../components/Layout';
import ProjectOverview from '../components/project/ProjectOverview';
import ProjectSuivi from '../components/project/ProjectSuivi';
import ProjectDocuments from '../components/project/ProjectDocuments';
import ProjectFinances from '../components/project/ProjectFinances';
import ElementsDashboardCard from '../components/project/ElementsDashboardCard';
import ImporterDialog from '../components/project/ImporterDialog';
import ProjectTasks from '../components/project/ProjectTasks';
import ProjectConception from '../components/project/ProjectConception';
import AvenantsADecider from '../components/project/AvenantsADecider';
import ProjectMarches from '../components/project/ProjectMarches';
import ProjectGallery from '../components/project/ProjectGallery';
import ProjectReception from '../components/project/ProjectReception';
import ProjectVisas from '../components/project/visas/ProjectVisas';
// R6 (RP73-79) — paiements côté client, point MOA
import ProjectPaiements from '../components/project/ProjectPaiements';
// A5 (D29) — l'entreprise dépose sa situation du mois
import EntrepriseSituations from '../components/project/EntrepriseSituations';
import PointMOA from '../components/project/PointMOA';
import useReglagesClient from '../hooks/useReglagesClient';
import DeleteChantierSecureDialog from '../components/project/DeleteChantierSecureDialog';
import AIChat from '../components/AIChat';
import { isClientUser, projectRights, MOE_ROLES } from '../components/project/projectRights';
import NotificationBell from '../components/NotificationBell';
import { 
  ArrowLeft,
  PencilSimple,
  Trash,
  Robot,
  House,
  ClipboardText,
  Files,
  CurrencyEur,
  ChatCircle,
  CheckSquare,
  PencilLine,
  Handshake,
  Plus,
  Images,
  Flag,
  Stamp,
  Wallet,
  Presentation,
  HardHat,
  ArrowSquareIn
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iter63b — Libellés courts pour le badge role_projet (header chantier).
// RP02 — plus de badge « Vue Entreprise / Vue Client / Vue Consultant » :
// étiquettes techniques sans valeur pour les externes (09-VOCABULAIRE §7).
// Seuls les rôles internes à la maîtrise d'œuvre gardent un badge.
const ROLE_PROJET_BADGE = {
  moe_principal: ROLE_PROJET.moe_principal,
  moe_membre: ROLE_PROJET.moe_membre,
};

// Fluidité B2 — icône de chacun des 6 onglets (+ Documents).
const ICONES_ONGLETS = {
  synthese: House,
  conception: PencilLine,
  marches: Handshake,
  chantier: HardHat,
  argent: CurrencyEur,
  reception: Flag,
  documents: Files,
};

// Vues d'un chantier (anciens onglets) — avec rôles autorisés. Fluidité B2 : elles ne
// sont plus affichées une par une ; elles sont regroupées en 6 onglets qui suivent le
// chantier (lib/ongletsChantier.js › ONGLETS_CHANTIER). Les droits restent calculés ici.
// RP01 — le client (MOA) ne voit plus « Agenda » ni « Finances » : le serveur lui
// refuse les réunions (/meetings) et les factures (/factures), l'écran restait vide
// ou à 0 €. Il garde Vue d'ensemble, Suivi chantier (planning publié) et Documents.
const projectTabs = [
  { id: 'overview', name: 'Vue d\'ensemble', icon: House, roles: ['admin', 'architect', 'collaborateur', 'amo', 'intervenant', 'client', 'entreprise'] },
  { id: 'conception', name: 'Conception', icon: PencilLine, roles: ['admin', 'architect', 'collaborateur', 'intervenant'] },
  { id: 'marches', name: 'Marchés', icon: Handshake, roles: ['admin', 'architect', 'collaborateur', 'amo', 'entreprise', 'intervenant'] },
  { id: 'suivi', name: 'Suivi chantier', icon: ClipboardText, roles: ['admin', 'architect', 'collaborateur', 'amo', 'intervenant', 'client'] },
  { id: 'documents', name: 'Documents', icon: Files, roles: ['admin', 'architect', 'collaborateur', 'amo', 'intervenant', 'client', 'entreprise'] },
  // RP13 / D15 — l'onglet chantier « Agenda » (ex-« Réunions », id 'planning') est retiré :
  // il lisait et créait des comptes rendus (`meetings`) et produisait des CR fantômes.
  // Le calendrier des réunions (objet `evenements`) est dans Suivi chantier › Réunions.
  // RP52-54 / D22 — circuit VISA des documents d'exécution (MOE, partenaires, entreprises).
  { id: 'visas', name: 'Visas', icon: Stamp, roles: ['admin', 'architect', 'collaborateur', 'intervenant', 'entreprise'] },
  { id: 'taches', name: OBJETS.actions, icon: CheckSquare, roles: ['admin', 'architect', 'collaborateur', 'intervenant', 'entreprise'] },
  // D.FIL.6 — Galerie photos (superviseurs MOE only, V1)
  { id: 'galerie', name: 'Galerie', icon: Images, roles: ['admin', 'architect', 'collaborateur', 'amo', 'intervenant'] },
  // Fluidité B2 — « Plans » (plans en vigueur annotés, lot A4) sous l'onglet Chantier ;
  // mêmes rôles que Documents › Plans en vigueur.
  { id: 'plans', name: 'Plans', icon: Files, roles: ['admin', 'architect', 'collaborateur', 'intervenant', 'entreprise'] },
  // R4 (RP47-50) — réception : réserves par lot, PV signé par le client, suivi de la levée
  { id: 'reception', name: OBJETS.reception, icon: Flag, roles: ['admin', 'architect', 'collaborateur', 'amo', 'intervenant', 'client', 'entreprise'] },
  { id: 'finances', name: 'Finances', icon: CurrencyEur, roles: ['admin', 'architect', 'amo'] },
  // RP73-75 / D31 — « Paiements » du client : situations certifiées, seulement si
  // l'architecte a activé « le client voit les montants » sur ce chantier.
  { id: 'paiements', name: 'Paiements', icon: Wallet, roles: ['client'] },
  // A5 / D29 — « Mes situations » de l'entreprise (dépôt de la situation du mois)
  { id: 'situations', name: 'Mes situations', icon: Wallet, roles: ['entreprise'] },
  // RP76-79 — « Point MOA » : budget, planning comparé, risques et décisions (AMO, architecte).
  { id: 'point-moa', name: 'Point MOA', icon: Presentation, roles: ['admin', 'architect', 'amo'] },
];

// PLA-02 — onglets calculés sur le rôle PROJET (ce que le serveur autorise vraiment
// sur ce chantier), et non plus sur le rôle global : un collaborateur de rôle global
// « Intervenant » membre MOE retrouve tous les onglets ; un partenaire (BET) ou un AMO
// (consultant) et un client ne voient plus d'onglet vide (Galerie, Finances : 403).
// Tant que le rôle projet n'est pas connu, l'ancien filtre par rôle global s'applique.
const TABS_BY_ROLE_PROJET = {
  archi: ['overview', 'conception', 'marches', 'suivi', 'documents', 'visas', 'taches', 'plans', 'galerie', 'reception', 'finances', 'point-moa'],
  moe_principal: ['overview', 'conception', 'marches', 'suivi', 'documents', 'visas', 'taches', 'plans', 'galerie', 'reception', 'finances', 'point-moa'],
  moe_membre: ['overview', 'conception', 'marches', 'suivi', 'documents', 'visas', 'taches', 'plans', 'galerie', 'reception', 'finances', 'point-moa'],
  // RP39 / D6 — Finances : seulement si « Voir les finances » est accordé (économiste).
  consultant: ['overview', 'conception', 'marches', 'suivi', 'documents', 'visas', 'taches', 'plans', 'reception', 'finances'],
  entreprise: ['overview', 'marches', 'documents', 'visas', 'taches', 'plans', 'reception', 'situations'],
  client: ['overview', 'suivi', 'documents', 'reception', 'paiements'],
  // RP40-41 / D7 — AMO « côté MOA » : ce que voit le client + Marchés (lots, marchés,
  // avenants, offres après la date limite), Finances (situations certifiées, lecture)
  // et Galerie. Ni Conception (CCTP / DCE / plans : cuisine interne), ni Visas, ni
  // Actions (doublon de « Mes tâches »). RP76-79 : + « Point MOA ».
  amo: ['overview', 'marches', 'suivi', 'documents', 'galerie', 'reception', 'finances', 'point-moa'],
};

function isTabVisible(tab, { roleGlobal, roleProjet, perms, montantsClient }) {
  // RP74 / D31 — « Paiements » : seulement si le client voit les montants
  if (tab.id === 'paiements' && !montantsClient) return false;
  const allowed = roleProjet ? TABS_BY_ROLE_PROJET[roleProjet] : null;
  if (!allowed) return !tab.roles || tab.roles.includes(roleGlobal);
  if (!allowed.includes(tab.id)) return false;
  // Point MOA (montants) : collaborateur seulement avec « Voir les finances » (D9)
  if (tab.id === 'point-moa' && roleProjet === 'moe_membre') return !!(perms && perms.can_see_finances);
  // Finances : uniquement si le droit « voir les finances » est effectif (surcharges comprises)
  if (tab.id === 'finances') return !!(perms && perms.can_see_finances);
  return true;
}

export default function ChantierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canEdit, isAdmin } = useAuth();
  // Iter 89 — Support du query param ?tab=<id> pour deep-linking (notamment
  // depuis ImportProjectWizard qui redirige vers ?tab=suivi).
  const [searchParams] = useSearchParams();

  const [chantier, setChantier] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fluidité B2 — { onglet, vue } : l'un des 6 onglets et la vue ouverte dedans.
  // Les anciens liens des e-mails et notifications (`?tab=suivi&sub=crc`, `?tab=taches`,
  // `?tab=finances`, `?tab=point-moa`, `?tab=planning`…) sont redirigés par
  // resoudreOnglet (lib/ongletsChantier.js) vers le bon onglet.
  const lireLien = (sp) => resoudreOnglet(sp.get('tab'), sp.get('sub'), {
    element_id: sp.get('element_id'), taches: sp.get('taches'), action: sp.get('action'),
  });
  const [nav, setNav] = useState(() => lireLien(searchParams));
  // CHA-15 — vue demandée à Suivi (Réunions, Comptes rendus, Planning) : `at` change à
  // chaque demande, pour rouvrir la liste même si la vue est déjà affichée.
  const [suiviTabRequest, setSuiviTabRequest] = useState(() => {
    const v = lireLien(searchParams).vue;
    return VUES_CHANTIER[v]?.suivi ? { tab: VUES_CHANTIER[v].suivi, at: 0 } : null;
  });
  const suiviSynchro = useRef(null);
  const derniereVue = useRef({});
  const allerA = ({ onglet, vue }) => {
    suiviSynchro.current = null;
    setNav({ onglet, vue });
    if (VUES_CHANTIER[vue]?.suivi) setSuiviTabRequest({ tab: VUES_CHANTIER[vue].suivi, at: Date.now() });
  };
  // Un lien profond suivi alors que la page est déjà ouverte (notification) : on suit.
  const lienCourant = searchParams.toString();
  useEffect(() => {
    if (lienCourant) allerA(lireLien(searchParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lienCourant]);
  // Navigation interne (Synthèse, Conception…) : accepte les anciens identifiants
  // d'onglet (« planning » → Chantier › Réunions, « taches » → Chantier › Actions…).
  const navigateTab = (tabId, sub) => allerA(resoudreOnglet(tabId, sub));
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  // Remonte la vue après un import pour relire ses données.
  const [rechargement, setRechargement] = useState(0);
  
  // Edit form
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [deleteSecureOpen, setDeleteSecureOpen] = useState(false);

  // Iter63b — récupère le rôle projet pour badge + filtrage onglets
  const { role_projet, perms } = usePermissions(id);
  // RP05 / D30 — pas d'assistant pour le client (MOA).
  const isClient = isClientUser(user?.role, role_projet);
  // RP74 / D31 — réglage du chantier « le client voit les montants » (client seulement)
  const { reglage: reglageClient } = useReglagesClient(id, isClient);
  const montantsClient = !!(reglageClient && reglageClient.client_voit_montants);
  const tabVisible = (tab) => isTabVisible(tab, { roleGlobal: user?.role, roleProjet: role_projet, perms, montantsClient });
  const visibleTabs = projectTabs.filter(tabVisible);
  // Fluidité B2 — une vue est visible si le droit de l'ancien onglet l'est ; un onglet
  // est visible s'il a au moins une vue visible (plus d'onglet vide ni refusé).
  const vueVisible = (v) => !!VUES_CHANTIER[v] && visibleTabs.some((t) => t.id === VUES_CHANTIER[v].droit);
  const ongletsVisibles = ONGLETS_CHANTIER
    .map((o) => ({ ...o, vuesVisibles: o.vues.filter(vueVisible) }))
    .filter((o) => o.vuesVisibles.length > 0);
  // RP01 — un lien profond (?tab=finances…) vers un onglet masqué pour ce rôle
  // retombe sur la Synthèse au lieu d'afficher un écran refusé.
  const ongletActif = ongletsVisibles.find((o) => o.id === nav.onglet) || ongletsVisibles[0] || { id: 'synthese', vuesVisibles: ['synthese'] };
  const vueActive = ongletActif.vuesVisibles.includes(nav.vue) ? nav.vue : ongletActif.vuesVisibles[0];
  // Suivi (Réunions, Comptes rendus, Planning) ouvert par défaut dans un onglet :
  // on lui demande la bonne vue.
  // (même objet tant que la vue ne change pas : Suivi ne se réinitialise pas à chaque rendu)
  const suiviVoulu = VUES_CHANTIER[vueActive]?.suivi || null;
  const suiviDemande = useMemo(() => {
    if (!suiviVoulu || (suiviTabRequest && suiviTabRequest.tab === suiviVoulu)) return suiviTabRequest;
    // Suivi a changé de vue de lui-même (CR créé depuis une réunion) : rien à redemander.
    if (suiviSynchro.current === suiviVoulu) return suiviTabRequest;
    return { tab: suiviVoulu, at: 0 };
  }, [suiviVoulu, suiviTabRequest]);
  // Suivi signale sa vue (ex. « Comptes rendus » après la création d'un CR) : l'onglet suit.
  const surVueSuivi = (sub) => {
    const vue = Object.keys(VUES_CHANTIER).find((k) => VUES_CHANTIER[k].suivi === sub);
    if (!vue || vue === vueActive) return;
    suiviSynchro.current = sub;
    setNav({ onglet: 'chantier', vue });
  };
  // Dernière vue ouverte dans chaque onglet (Chantier › Comptes rendus…), pour y revenir.
  const ouvrirOnglet = (o) => {
    const vue = o.vuesVisibles.includes(derniereVue.current[o.id]) ? derniereVue.current[o.id] : o.vuesVisibles[0];
    allerA({ onglet: o.id, vue });
  };
  useEffect(() => { derniereVue.current[ongletActif.id] = vueActive; }, [ongletActif.id, vueActive]);

  // Une seule entrée « Importer » (P2#17) : ce que la personne peut importer ici.
  const rights = projectRights({ userRole: user?.role, roleProjet: role_projet, perms });
  const roleEffectif = role_projet || (['admin', 'architect'].includes(user?.role) ? 'archi' : (user?.role === 'collaborateur' ? 'moe_membre' : null));
  const estMoe = MOE_ROLES.includes(roleEffectif);
  const peutModifierFiche = canEdit && (['admin', 'architect'].includes(user?.role) || !!perms.can_modify_chantier_settings);
  const droitsImport = {
    dpgfWord: ['archi', 'moe_principal'].includes(roleEffectif),
    dpgfExcel: rights.canImportDpgf,
    dpgfDocuments: rights.canImportDpgf,
    devis: rights.canWriteMarches,
    chantierEnCours: peutModifierFiche,
    comptesRendus: estMoe,
    whatsapp: ['admin', 'architect', 'collaborateur'].includes(user?.role) && estMoe,
    documents: canEdit && estMoe,
  };
  const peutImporter = Object.values(droitsImport).some(Boolean);
  const apresImport = (cle) => {
    const cible = { dpgfWord: 'conception', dpgfDocuments: 'conception', dpgfExcel: 'marches', devis: 'marches', comptesRendus: 'crc', whatsapp: 'actions' }[cle];
    if (cle === 'chantierEnCours') fetchChantier();
    if (cible && vueVisible(cible)) allerA(resoudreOnglet(cible));
    setRechargement((n) => n + 1);
  };

  useEffect(() => {
    fetchChantier();
  }, [id]);

  const fetchChantier = async (retries = 2) => {
    setLoadError(false);
    for (let i = 0; i <= retries; i++) {
      try {
        const { data } = await axios.get(`${API_URL}/api/chantiers/${id}`, { withCredentials: true });
        setChantier(data);
        setEditForm({ ...data, etape: etapeChantier(data), en_pause: data.status === 'en_pause' });
        setLoading(false);
        return;
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 403) {
          navigate('/chantiers');
          return;
        }
        if (i < retries) await new Promise(r => setTimeout(r, 1000));
      }
    }
    setLoading(false);
    setLoadError(true);
  };

  const handleUpdateChantier = async (e) => {
    e.preventDefault();
    try {
      // B1 — seuls les champs modifiables sont envoyés ; l'étape (et la pause) passent par
      // la correction d'étape côté serveur, le statut en est déduit.
      const payload = {
        name: editForm.name, address: editForm.address, progress: editForm.progress,
        owner_name: editForm.owner_name, budget: editForm.budget,
      };
      if (editForm.etape && editForm.etape !== etapeChantier(chantier)) payload.etape = editForm.etape;
      if (!!editForm.en_pause !== (chantier.status === 'en_pause')) payload.en_pause = !!editForm.en_pause;
      await axios.put(`${API_URL}/api/chantiers/${id}`, payload, { withCredentials: true });
      toast.success('Chantier mis à jour');
      setEditOpen(false);
      fetchChantier();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteChantier = () => {
    // Workflow sécurisé en 3 étapes (archive + code email + saisie nom)
    setDeleteSecureOpen(true);
  };

  const handleDeletedSuccess = () => {
    toast.success('Chantier supprimé définitivement');
    navigate('/chantiers');
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-12 mb-6" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  if (!chantier) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 text-center py-24">
          <p className="text-[#6B6B6B] mb-4">{loadError ? 'Impossible de charger le chantier' : 'Chantier introuvable'}</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/chantiers"><Button variant="outline" className="rounded-none">Retour aux chantiers</Button></Link>
            {loadError && <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" onClick={() => { setLoading(true); fetchChantier(); }}>Recharger</Button>}
          </div>
        </div>
      </Layout>
    );
  }

  const renderTabContent = () => {
    const userRole = user?.role;
    switch (vueActive) {
      case 'synthese':
        // Synthèse du dossier (contenu : lot B1) — emplacement unique en tête du chantier.
        return <ProjectOverview chantier={chantier} onUpdate={fetchChantier} onNavigateTab={navigateTab} />;
      case 'conception':
        return <ProjectConception chantierId={id} chantierName={chantier.name} onNavigateTab={navigateTab} onImporter={peutImporter ? () => setImporterOpen(true) : undefined} />;
      case 'marches':
        return <ProjectMarches chantierId={id} onImporter={peutImporter ? () => setImporterOpen(true) : undefined} />;
      case 'reunions':
      case 'crc':
      case 'planning':
        // Chantier › Réunions, Comptes rendus, Planning : le même écran Suivi, piloté
        // par la barre de l'onglet (plus de second menu « Actions » dedans).
        return (
          <ProjectSuivi
            chantierId={id}
            userRole={userRole}
            tabRequest={suiviDemande}
            navigationExterne
            onSubTabChange={surVueSuivi}
          />
        );
      case 'actions':
        // Chantier › Actions : l'unique liste des actions (P3#6).
        return (
          <>
            <ElementsDashboardCard chantierId={id} />
            <ProjectTasks chantierId={id} />
          </>
        );
      case 'visas':
        return <ProjectVisas chantierId={id} />;
      case 'plans':
        return <ProjectDocuments chantierId={id} userRole={userRole} chantier={chantier} plansSeulement />;
      case 'galerie':
        return <ProjectGallery chantierId={id} />;
      case 'reception':
        return <ProjectReception chantierId={id} />;
      case 'finances':
        return <ProjectFinances chantierId={id} chantier={chantier} />;
      case 'paiements':
        return <ProjectPaiements chantierId={id} />;
      case 'situations':
        return <EntrepriseSituations chantierId={id} />;
      case 'point-moa':
        return <PointMOA chantierId={id} />;
      case 'documents':
        return <ProjectDocuments chantierId={id} userRole={userRole} chantier={chantier} />;
      default:
        return <ProjectOverview chantier={chantier} onUpdate={fetchChantier} onNavigateTab={navigateTab} />;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen" data-testid="chantier-detail-page">
        {/* Project Header */}
        <div className="bg-white border-b border-[#E0E0DB] sticky top-0 lg:top-0 z-40" style={{ paddingTop: 'max(0px, env(safe-area-inset-top))' }}>
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <Link
                  to="/chantiers"
                  className="flex-shrink-0 mt-0.5 p-2 border border-[#E0E0DB] hover:bg-[#2C2C2C] hover:text-white hover:border-[#2C2C2C] transition-colors"
                  data-testid="back-to-projects"
                  title="Retour aux chantiers"
                >
                  <ArrowLeft size={18} weight="bold" />
                </Link>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl sm:text-2xl text-[#2C2C2C] truncate" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      {chantier.name}
                    </h1>
                    {/* B1 — l'en-tête dit l'étape (plus seulement « EN COURS ») */}
                    <span
                      className={`text-[10px] sm:text-xs px-2 py-0.5 uppercase tracking-wider font-bold ${ETAPE_CLASSE[etapeChantier(chantier)] || ''}`}
                      data-testid="badge-etape"
                      title="Étape du chantier"
                    >
                      {chantier.role_projet === 'client' ? libelleEtape(etapeChantier(chantier), true) : (chantier.etape_libelle || libelleEtape(etapeChantier(chantier)))}
                    </span>
                    {['prospection', 'en_pause'].includes(chantier.status) && (
                      <span className="text-[10px] px-2 py-0.5 uppercase tracking-wider font-medium border border-[#E0E0DB] text-[#6B6B6B]">
                        {STATUT_CHANTIER[chantier.status]}
                      </span>
                    )}
                    {ROLE_PROJET_BADGE[role_projet] && (
                      <span
                        className="text-[10px] px-2 py-0.5 uppercase tracking-wider font-medium bg-[#1E3A5F] text-white"
                        data-testid="badge-role-projet"
                        title="Votre rôle sur ce chantier"
                      >
                        {ROLE_PROJET_BADGE[role_projet]}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-[#6B6B6B] mt-0.5">{chantier.address || chantier.adresse || ''}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap pl-12 lg:pl-0">
                <NewSinceLastCRCBadge
                  chantierId={id}
                  onClick={() => {
                    // CHA-15 — ouvrir directement Chantier › Comptes rendus
                    allerA({ onglet: 'chantier', vue: 'crc' });
                  }}
                />
                <OverridesIndicator
                  chantierId={id}
                  canSee={role_projet === 'archi' || role_projet === 'moe_principal'}
                />
                <NotificationBell />

                {/* AI Chat Button — RP05 / D30 : masqué pour le client (MOA) et l'AMO (côté MOA, RP40) */}
                {!isClient && role_projet !== 'amo' && (
                <Sheet open={aiChatOpen} onOpenChange={setAiChatOpen}>
                  <SheetTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="gap-2 rounded-none border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                      data-testid="ai-chat-button"
                    >
                      <Robot size={18} weight="bold" />
                      Assistant
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-full sm:w-[500px] p-0">
                    <AIChat chantierId={id} chantierName={chantier.name} onClose={() => setAiChatOpen(false)}
                      onVoirSynthese={() => { setAiChatOpen(false); navigateTab('synthese'); }} />
                  </SheetContent>
                </Sheet>
                )}

                {/* Fluidité B2 (P2#17) — une seule entrée « Importer » : elle demande quoi importer
                    (DPGF Word / Excel, devis, chantier en cours, CR, WhatsApp, documents). */}
                {peutImporter && (
                  <Button variant="outline" className="gap-2 rounded-none text-[#D4880F] border-[#D4880F] hover:bg-[#D4880F] hover:text-white" onClick={() => setImporterOpen(true)} data-testid="onboarding-button">
                    <ArrowSquareIn size={16} weight="bold" />
                    {BOUTONS.importer}
                  </Button>
                )}
                {/* RP34 — « Modifier » : associé (rôle global) ou droit « fiche du projet » (le collaborateur ne modifie pas la fiche) */}
                {canEdit && (['admin', 'architect'].includes(user?.role) || perms.can_modify_chantier_settings) && (
                  <>
                    <Dialog open={editOpen} onOpenChange={setEditOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2 rounded-none" data-testid="edit-chantier-button">
                          <PencilSimple size={18} />
                          Modifier
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier le chantier</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleUpdateChantier} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label>Nom du chantier</Label>
                              <Input value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} className="rounded-none" />
                            </div>
                            <div className="col-span-2">
                              <Label>Adresse</Label>
                              <Input value={editForm.address || ''} onChange={e => setEditForm({...editForm, address: e.target.value})} className="rounded-none" />
                            </div>
                            <div>
                              <Label>Étape</Label>
                              <Select value={editForm.etape} onValueChange={v => setEditForm({...editForm, etape: v})}>
                                <SelectTrigger className="rounded-none" data-testid="edit-etape-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {ETAPES_CHANTIER.map((e) => <SelectItem key={e.cle} value={e.cle}>{e.libelle}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <label className="flex items-center gap-2 text-xs text-[#6B6B6B] mt-2">
                                <input type="checkbox" checked={!!editForm.en_pause} onChange={e => setEditForm({...editForm, en_pause: e.target.checked})} />
                                Chantier en pause
                              </label>
                            </div>
                            <div>
                              <Label>Progression (%)</Label>
                              <Input type="number" min="0" max="100" value={editForm.progress || 0} onChange={e => setEditForm({...editForm, progress: parseInt(e.target.value)})} className="rounded-none" />
                            </div>
                            <div>
                              <Label>Client (maître d'ouvrage)</Label>
                              <Input value={editForm.owner_name || ''} onChange={e => setEditForm({...editForm, owner_name: e.target.value})} className="rounded-none" />
                            </div>
                            <div>
                              <Label>Budget travaux (€ HT)</Label>
                              <Input type="number" value={editForm.budget || ''} onChange={e => setEditForm({...editForm, budget: parseFloat(e.target.value)})} className="rounded-none" />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">Annuler</Button>
                            <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Enregistrer</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {isAdmin && (
                      <Button variant="outline" className="gap-2 rounded-none text-[#8B3A3A] hover:bg-red-50" onClick={handleDeleteChantier} data-testid="delete-chantier-button">
                        <Trash size={18} />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Fluidité B2 — 6 onglets qui suivent le chantier (+ Documents, à part) ; tous
              visibles à 1440 px et sur iPad (icônes seulement sur grand écran). Selon l'étape,
              les onglets hors sujet sont en retrait mais restent accessibles. */}
          <div className="px-4 sm:px-6 border-t border-[#E0E0DB]">
            <nav className="flex gap-0 -mb-px overflow-x-auto" data-testid="onglets-chantier">
              {ongletsVisibles.map((o) => {
                const Icon = ICONES_ONGLETS[o.id] || House;
                const isActive = ongletActif.id === o.id;
                const horsEtape = !isActive && ongletHorsEtape(o.id, chantier);
                return (
                  <button
                    key={o.id}
                    onClick={() => ouvrirOnglet(o)}
                    title={horsEtape ? `${o.nom} — pas l'étape en cours, reste accessible` : undefined}
                    className={`flex items-center gap-2 px-3 xl:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      o.id === 'documents' ? 'ml-auto border-l border-l-[#E0E0DB]' : ''
                    } ${
                      isActive
                        ? 'border-b-[#C75B39] text-[#C75B39]'
                        : `border-b-transparent hover:text-[#2C2C2C] hover:border-b-[#E0E0DB] ${horsEtape ? 'text-[#A8A8A3]' : 'text-[#6B6B6B]'}`
                    }`}
                    data-testid={`project-tab-${o.id}`}
                    data-hors-etape={horsEtape ? 'oui' : undefined}
                  >
                    <Icon size={18} weight={isActive ? 'fill' : 'regular'} className="hidden xl:inline" />
                    {o.nom}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* R5 / RP67-68 (D30) — le client accepte ou refuse les avenants qui l'engagent */}
          {role_projet === 'client' && <AvenantsADecider chantierId={id} />}
          {/* Chantier (Réunions, Comptes rendus, Actions, Visas, Plans, Galerie, Planning) et
              Argent (Finances, Paiements, Point MOA) : une barre de vues, selon les droits. */}
          {ongletActif.vuesVisibles.length > 1 && (
            <div className="flex flex-wrap gap-1 mb-6 bg-[#F5F5F3] p-1 w-fit max-w-full" role="tablist" data-testid={`vues-${ongletActif.id}`}>
              {ongletActif.vuesVisibles.map((v) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={vueActive === v}
                  onClick={() => allerA({ onglet: ongletActif.id, vue: v })}
                  className={`px-3 py-1.5 text-sm whitespace-nowrap ${vueActive === v ? 'bg-white text-[#2C2C2C] font-medium shadow-sm' : 'text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
                  data-testid={`vue-${v}`}
                >
                  {VUES_CHANTIER[v].nom}
                </button>
              ))}
            </div>
          )}
          <div key={`${VUES_CHANTIER[vueActive]?.suivi ? 'suivi' : vueActive}-${rechargement}`} className="contents">
            {renderTabContent()}
          </div>
        </div>

        {/* Une seule entrée « Importer » (P2#17) */}
        <ImporterDialog
          open={importerOpen}
          onOpenChange={setImporterOpen}
          chantierId={id}
          chantier={chantier}
          droits={droitsImport}
          onImporte={apresImport}
          onAllerDocuments={() => allerA({ onglet: 'documents', vue: 'documents' })}
        />

        {/* Suppression sécurisée (archive + code email + nom) */}
        <DeleteChantierSecureDialog
          open={deleteSecureOpen}
          onOpenChange={setDeleteSecureOpen}
          chantierId={id}
          chantierNom={chantier?.name || ''}
          onDeleted={handleDeletedSuccess}
        />
      </div>
    </Layout>
  );
}
